import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { Loader2 } from 'lucide-react';

const pricingTypes = [
  { value: 'ADDON', label: 'Add-on (extra cost)' },
  { value: 'REPLACEMENT', label: 'Replacement (price difference)' },
];

const pricingUnits = [
  { value: 'PER_PERSON', label: 'Per Person' },
  { value: 'PER_BOOKING', label: 'Per Booking (flat)' },
  { value: 'PERCENTAGE', label: 'Percentage of base' },
];

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  shortLabel: z.string().optional(),
  pricingType: z.enum(['ADDON', 'REPLACEMENT']),
  pricingUnit: z.enum(['PER_PERSON', 'PER_BOOKING', 'PERCENTAGE']),
  price: z.number().min(0),
  currency: z.string(),
  allocationId: z.string().optional(),
  unitsPerBooking: z.number().min(1),
  imageUrl: z.string().optional(),
  sortOrder: z.number(),
  isDefault: z.boolean(),
});

type FormData = z.infer<typeof formSchema>;

interface OptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  option?: {
    id: string;
    name: string;
    description: string | null;
    shortLabel: string | null;
    pricingType: string;
    pricingUnit: string;
    price: number;
    currency: string;
    allocationId: string | null;
    unitsPerBooking: number;
    imageUrl: string | null;
    sortOrder: number;
    isDefault: boolean;
  };
  onSuccess?: () => void;
}

export function OptionModal({
  open,
  onOpenChange,
  groupId,
  option,
  onSuccess,
}: OptionModalProps) {
  const isEditing = !!option;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      shortLabel: '',
      pricingType: 'ADDON',
      pricingUnit: 'PER_PERSON',
      price: 0,
      currency: 'USD',
      allocationId: '',
      unitsPerBooking: 1,
      imageUrl: '',
      sortOrder: 0,
      isDefault: false,
    },
  });

  // Fetch allocations for linking
  const { data: allocations } = trpc.allocation.list.useQuery({
    page: 1,
    status: 'ACTIVE',
  });

  useEffect(() => {
    if (option) {
      form.reset({
        name: option.name,
        description: option.description || '',
        shortLabel: option.shortLabel || '',
        pricingType: option.pricingType as FormData['pricingType'],
        pricingUnit: option.pricingUnit as FormData['pricingUnit'],
        price: option.price,
        currency: option.currency,
        allocationId: option.allocationId || '',
        unitsPerBooking: option.unitsPerBooking,
        imageUrl: option.imageUrl || '',
        sortOrder: option.sortOrder,
        isDefault: option.isDefault,
      });
    } else {
      form.reset({
        name: '',
        description: '',
        shortLabel: '',
        pricingType: 'ADDON',
        pricingUnit: 'PER_PERSON',
        price: 0,
        currency: 'USD',
        allocationId: '',
        unitsPerBooking: 1,
        imageUrl: '',
        sortOrder: 0,
        isDefault: false,
      });
    }
  }, [option, form]);

  const createMutation = trpc.packageOption.createOption.useMutation({
    onSuccess: () => {
      showSuccessToast('Option created');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to create option');
    },
  });

  const updateMutation = trpc.packageOption.updateOption.useMutation({
    onSuccess: () => {
      showSuccessToast('Option updated');
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message || 'Failed to update option');
    },
  });

  const onSubmit = (data: FormData) => {
    const payload = {
      ...data,
      allocationId: data.allocationId || undefined,
      imageUrl: data.imageUrl || undefined,
      description: data.description || undefined,
      shortLabel: data.shortLabel || undefined,
    };

    if (isEditing && option) {
      updateMutation.mutate({
        id: option.id,
        ...payload,
      });
    } else {
      createMutation.mutate({
        groupId,
        ...payload,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Option' : 'Add Option'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="e.g., 5-Star Hilton Hotel"
                {...form.register('name')}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="shortLabel">Short Label</Label>
              <Input
                id="shortLabel"
                placeholder="e.g., Hilton 5*"
                {...form.register('shortLabel')}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe what this option includes"
              rows={2}
              {...form.register('description')}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Pricing</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pricingType">Pricing Type</Label>
                <Select
                  value={form.watch('pricingType')}
                  onValueChange={(value) =>
                    form.setValue('pricingType', value as FormData['pricingType'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pricingTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pricingUnit">Pricing Unit</Label>
                <Select
                  value={form.watch('pricingUnit')}
                  onValueChange={(value) =>
                    form.setValue('pricingUnit', value as FormData['pricingUnit'])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pricingUnits.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="price">
                  Price{' '}
                  {form.watch('pricingUnit') === 'PERCENTAGE' ? '(%)' : ''}
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  {...form.register('price', { valueAsNumber: true })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={form.watch('currency')}
                  onValueChange={(value) => form.setValue('currency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="TRY">TRY</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Inventory (Optional)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="allocationId">Link to Allocation</Label>
                <Select
                  value={form.watch('allocationId') || 'none'}
                  onValueChange={(value) =>
                    form.setValue('allocationId', value === 'none' ? '' : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No inventory tracking" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No inventory tracking</SelectItem>
                    {allocations?.allocations.map((alloc) => (
                      <SelectItem key={alloc.id} value={alloc.id}>
                        {alloc.name} ({alloc.availableUnits} available)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unitsPerBooking">Units per Booking</Label>
                <Input
                  id="unitsPerBooking"
                  type="number"
                  min="1"
                  {...form.register('unitsPerBooking', { valueAsNumber: true })}
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Display</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image URL</Label>
                <Input
                  id="imageUrl"
                  placeholder="https://..."
                  {...form.register('imageUrl')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  {...form.register('sortOrder', { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="space-y-0.5">
                <Label htmlFor="isDefault">Default Selection</Label>
                <p className="text-sm text-muted-foreground">
                  Pre-select this option by default
                </p>
              </div>
              <Switch
                id="isDefault"
                checked={form.watch('isDefault')}
                onCheckedChange={(checked) => form.setValue('isDefault', checked)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
